import { IPriceData } from 'Quotes/types/store';
import React, { FC, useEffect, useState } from 'react';
import { Supplier } from 'Quotes/types/supplier';
import { ProductType } from 'Quotes/types/productTypes';
import { ListAndSliderViewToggle } from 'Quotes/QuoteBuilder/components/Price/components/SupplierView/Toggle';
import SupplierSelectionDropdown from 'Quotes/QuoteBuilder/components/Price/components/SupplierSelectionDropdown';
import { ProductSubTypeFilter } from 'Quotes/QuoteBuilder/components/Price/components/ProductSubTypeFilter';
import { BandwidthFilter } from 'Quotes/QuoteBuilder/components/Price/components/BandwidthFilter';
import { ContractTermFilter } from 'Quotes/QuoteBuilder/components/Price/components/ContractTermFilter';
import styled from 'styled-components';
import { presentSupplierName } from 'Quotes/utils/supplierHelpers';
import { ExcludedSuppliers } from 'Admin/preferences/SupplierPreferences/SupplierPreferences';
import { setMonthlyDisplayPreference, useMonthlyDisplayPreference } from 'Quotes/utils/priceDisplay';
import ToggleButton from 'shared/components/molecules/ToggleButton';
import { ExportCSV } from './ExportCSV';

const FilterWrapper: FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <FilterRow>
    <FilterLabel>Filter</FilterLabel>
    {children}
  </FilterRow>
);

interface FiltersParams {
  readOnly: undefined | boolean;
  suppliersSortedByPresentationName: Supplier[];
  productType: ProductType;
  pricesHaveProductSubType: boolean;
  priceHasBandwidth: boolean;
  priceHasMultipleContractTerms: boolean;
}

const Filters = ({
  priceHasBandwidth,
  priceHasMultipleContractTerms,
  pricesHaveProductSubType,
  productType,
  readOnly,
  suppliersSortedByPresentationName,
}: FiltersParams) => {
  if (readOnly) return <FilterWrapper />;
  return (
    <FilterWrapper>
      <SupplierSelectionDropdown suppliers={suppliersSortedByPresentationName} productType={productType} />
      {pricesHaveProductSubType && <ProductSubTypeFilter />}
      {priceHasBandwidth && <BandwidthFilter />}
      {priceHasMultipleContractTerms && <ContractTermFilter />}
    </FilterWrapper>
  );
};

const getSuppliersSortedByPresentationName = (): Supplier[] => {
  return Object.values(Supplier)
    .filter((supplier) => !ExcludedSuppliers.includes(supplier))
    .sort((a, b) => {
      const aPresentName = presentSupplierName(a);
      const bPresentName = presentSupplierName(b);
      if (aPresentName < bPresentName) return -1;
      if (bPresentName < aPresentName) return 1;
      return 0;
    });
};

export function PricesToolbar({
  activeView,
  allPrices,
  onClick,
  filteredPrices,
  productType,
  readOnly,
  quoteId,
  shortId,
}: {
  allPrices: IPriceData[];
  activeView: 'list' | 'slider';
  onClick: (value: 'list' | 'slider') => void;
  readOnly: undefined | boolean;
  productType: ProductType;
  filteredPrices: IPriceData[];
  quoteId: string | undefined;
  shortId: string | undefined;
}) {
  const viewAsMonthly = useMonthlyDisplayPreference();
  const [priceHasBandwidth, setPriceHasBandwidth] = useState(false);
  const [multipleContractTerms, setMultipleContractTerms] = useState(false);

  const onViewAsMonthlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMonthlyDisplayPreference(event.target.checked);
  };

  useEffect(() => {
    setPriceHasBandwidth(allPrices.some((price) => !!price.bandwidth));
  }, [allPrices]);

  useEffect(() => {
    const allTermLengths = allPrices.map((price) => price.term_length_in_months);
    setMultipleContractTerms(new Set(allTermLengths).size > 1);
  }, [allPrices]);

  return (
    <ViewConfigRow>

      <ListAndSliderViewToggle activeView={activeView} onClick={onClick} />

      <Divider>&nbsp;</Divider>

      <MonthlyToggle
        label="View as monthly"
        checked={viewAsMonthly}
        onToggleChange={onViewAsMonthlyChange}
      />

      <Divider>&nbsp;</Divider>

      <Filters
        readOnly={readOnly}
        suppliersSortedByPresentationName={getSuppliersSortedByPresentationName()}
        productType={productType}
        pricesHaveProductSubType={filteredPrices.every((price) => price.product_sub_type !== null)}
        priceHasBandwidth={priceHasBandwidth}
        priceHasMultipleContractTerms={multipleContractTerms}
      />

      <Divider>&nbsp;</Divider>

      <ExportCSV quoteId={quoteId} shortId={shortId} />

    </ViewConfigRow>
  );
}

const Divider = styled.div`
  width: 1px;
  background-color: ${(props) => props.theme.colours.grey30};
`;

const ViewConfigRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1em 0 1.5em 0;

  .export-csv-row {

  }
    .export-csv-row button {
      margin: 0;
      padding: 0;
      font-size: 1em;  
    }
  `;

const MonthlyToggle = styled(ToggleButton)`
  &&.toggle {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    margin-bottom: 0;
  }

  .toggle-label {
    margin-left: 0;
    margin-right: 5px;
    top: 0;
  }
`;

const FilterRow = styled.div`
  display: flex;
  justify-content: end;
  column-gap: 1rem;
`;

const FilterLabel = styled.span`
  color: ${(props) => props.theme.colours.primaryA1};
  font-weight: bold;
  display: flex;
  align-items: center;
`;
